import type { PageTreeChild, PageTreeNode } from '../../components';
import { pathLabel } from '../creatives/page-labels';
import { SITE_DEFAULTS } from './selection';
import type { SiteSeoPage, SiteSeoRoute } from './seo-types';

/** The tree's own copy, translated by the caller. */
export type RouteTreeCopy = { siteDefaults: string; homepage: string; everyPage: string; footerOnly: string };

/** A page's label in `language`, else the default language, else `path`. A blank label counts as none. */
export const pageLabel = (
  page: SiteSeoPage | undefined,
  path: string,
  language: string,
  defaultLanguage: string,
): string => page?.label?.[language] || page?.label?.[defaultLanguage] || path;

/**
 * A route's name wherever the page names it (the tree, the editor's heading, the page-level notes): an owner's route
 * by its path; a site page by its label, else as Game Lists names a page the site does not name: the home page by the
 * hub's word (`homepage`, translated by the caller), an exact page by its last segment
 * ("/customer-support/betting-rules": Betting rules). A dynamic page keeps its path, which says what it covers.
 */
export const routeName = (
  route: SiteSeoRoute,
  page: SiteSeoPage | undefined,
  language: string,
  defaultLanguage: string,
  homepage: string,
): string => {
  if (route.origin === 'custom') return route.path;
  const named = pageLabel(page, '', language, defaultLanguage);
  if (named !== '') return named;
  if (route.path === '/') return homepage;
  return route.match === 'exact' ? pathLabel(route.path) : route.path;
};

/** `routes` in the order the tree shows them: the site's pages in the site's order (those it no longer has after
 *  them), then the owner's own routes in the draft's order. */
const inTreeOrder = (routes: SiteSeoRoute[], pages: SiteSeoPage[]): SiteSeoRoute[] => {
  const index = new Map(pages.map((p, i) => [p.path, i]));
  const at = (r: SiteSeoRoute): number => (r.origin === 'custom' ? pages.length + 1 : index.get(r.path) ?? pages.length);
  return routes
    .map((route, i) => ({ route, i }))
    .sort((a, b) => at(a.route) - at(b.route) || a.i - b.i)
    .map((x) => x.route);
};

/** Whether `top` is an ancestor of `path` at a segment boundary ("/casino" of "/casino/x", not of "/casinox"). The
 *  home page is nobody's: under it, every page would be one branch. */
const sitsUnder = (top: string, path: string): boolean => top !== '/' && path.startsWith(`${top}/`);

/**
 * The SEO page's routes as a two-level page tree: Site defaults first, then each top route with every route under its
 * path in its branch (a dynamic page and an owner's route included, so "/casino/:game" and "/casino/*" sit under
 * Casino). A route is a top route when no other exact route is an ancestor of its path; a deeper one goes under its
 * topmost ancestor. Hints name what a row covers: a dynamic page serves every page of its pattern, a wildcard only
 * the footer.
 */
export const routeTree = (
  routes: SiteSeoRoute[],
  pages: SiteSeoPage[],
  language: string,
  defaultLanguage: string,
  copy: RouteTreeCopy,
): PageTreeNode[] => {
  const pageOf = new Map(pages.map((p) => [p.path, p]));
  const ordered = inTreeOrder(routes, pages);
  const exact = ordered.filter((r) => r.match === 'exact').map((r) => r.path);
  const topOf = (path: string): string | undefined =>
    exact.filter((top) => sitsUnder(top, path)).sort((a, b) => a.length - b.length)[0];

  const child = (route: SiteSeoRoute): PageTreeChild => {
    const label = routeName(route, pageOf.get(route.path), language, defaultLanguage, copy.homepage);
    const hint = route.match === 'pattern' ? copy.everyPage : route.match === 'wildcard' ? copy.footerOnly : undefined;
    return hint ? { path: route.path, label, hint } : { path: route.path, label };
  };

  const tops = ordered.filter((r) => topOf(r.path) === undefined);
  return [
    { path: SITE_DEFAULTS, label: copy.siteDefaults, children: [] },
    ...tops.map((top) => ({
      ...child(top),
      children: ordered.filter((r) => topOf(r.path) === top.path).map(child),
    })),
  ];
};
